import { useEffect, useRef } from 'react';
import { scrollIntoView } from '../../../Common/navigation/utils';

export const IndexList = ({
  tocIndexList,
  dom,
  activeIndex,
  setActiveIndex,
  setToggleIndex,
}: {
  tocIndexList: Array<{ id: string; content: string }>;
  dom: HTMLElement;
  activeIndex: string;
  setActiveIndex: (index: string) => void;
  setToggleIndex?: (index: boolean) => void;
}) => {
  const headingRefs = useRef<Array<HTMLHeadingElement | null>>([]); // Array of refs for headings

  const tocRefs = useRef<(HTMLElement | null)[]>([]); // Initialize with proper type

  function isElementInViewport(el: HTMLElement, container: HTMLElement) {
    const elementRect = el.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();

    // Check if the element is within the container's visible area
    return (
      elementRect.top >= containerRect.top &&
      elementRect.bottom <= containerRect.bottom
    );
  }

  useEffect(() => {
    // Scroll the active TOC item into view
    if (window.innerWidth >= 768) {
      const activeTocItem = tocRefs.current.find(
        (item) => item?.dataset.tocIndex?.substring(1) === activeIndex
      );
      const container = document.querySelector('.TOC_index_Desktop');
      if (activeTocItem && container) {
        const isIndexVisible = isElementInViewport(
          activeTocItem,
          container as HTMLElement
        );

        if (!isIndexVisible) activeTocItem.scrollIntoView({ behavior: 'auto' });
      }
    }
  }, [activeIndex]); // Run this effect whenever the active heading changes

  useEffect(() => {
    if (dom) {
      const headings = dom.querySelectorAll('h2, h3');

      const sanitizedHeaders = [];
      for (let i = 0; i < headings.length; i++) {
        // to parse only heading present in content and rejection heading in FAQ and side CTA
        const curHeader = headings[i] as HTMLElement;
        if (curHeader.innerText.trim()) {
          sanitizedHeaders.push(curHeader);
        }
      }
      headingRefs.current = Array.from(
        sanitizedHeaders
      ) as HTMLHeadingElement[];

      const handleIntersection: IntersectionObserverCallback = (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveIndex(entry.target.id); // Update active heading
          }
        });
      };
      const offsetYAxis = window.innerHeight - 80 + 'px';

      const observer = new IntersectionObserver(handleIntersection, {
        root: null, // Observes relative to the viewport
        rootMargin: `0px 0px -${offsetYAxis} 0px`,
        threshold: 0, // Trigger as soon as the rootMargin condition is met
      });

      headingRefs.current.forEach((ref) => {
        if (ref) {
          observer.observe(ref);
        }
      });

      return () => {
        // Cleanup observer
        headingRefs.current.forEach((ref) => {
          if (ref) {
            observer.unobserve(ref);
          }
        });
      };
    }
    return;
  }, [dom]);

  const handleTocClick = (e: React.MouseEvent | React.TouchEvent) => {
    const clickedItem = e.target as HTMLElement;
    if (clickedItem.tagName === 'A') {
      e.preventDefault();
      const currentUrl = new URL(window.location.href);
      const sectionId = clickedItem.dataset.tocIndex?.substring(1) || '';
      // const targetId = clickedItem.getAttribute('href')?.substring(1) || '';
      currentUrl.hash = sectionId;
      setActiveIndex(sectionId);
      setToggleIndex && setToggleIndex(false);
      history.pushState(null, '', currentUrl.toString());
      scrollIntoView(
        document.querySelector(`#${sectionId}`) as HTMLElement,
        90,
        'auto'
      );
    }
  };

  return (
    <>
      <ul onClick={handleTocClick}>
        {tocIndexList?.map((item, index) => {
          return (
            <li
              key={item.id}
              className={`${activeIndex === item.id ? 'activeIndex' : ''} `}
              title={item.content}
            >
              <a
                className={`tocAnchorTag ${item.id}`}
                href={`#${item.id}`}
                data-toc-index={`#${item.id}`}
                ref={(item) => (tocRefs.current[index] = item)}
              >
                {item.content}
              </a>
            </li>
          );
        })}
      </ul>
    </>
  );
};
